// Ollama API Client
// https://github.com/ollama/ollama/blob/main/docs/api.md

import type { HttpRequest } from '../http.js'
import { createOllamaUtils } from './utils.js'

export interface OllamaGenerateArgs {
  model: string
  prompt: string
  options?: Record<string, unknown>
  stream?: boolean
  host?: string
}

export interface OllamaChatArgs {
  model: string
  messages: unknown[]
  tools?: unknown[]
  think?: boolean
  options?: Record<string, unknown>
  stream?: boolean
  host?: string
}

export interface OllamaChatCompletionsArgs {
  model: string
  messages: unknown[]
  temperature?: number
  max_tokens?: number
  tools?: unknown[]
  tool_choice?: unknown
  stream?: boolean
  host?: string
}

export interface OllamaEmbeddingsArgs {
  model: string
  prompt: string
  options?: Record<string, unknown>
  host?: string
}

export interface OllamaClient {
  generate(args: OllamaGenerateArgs): Promise<any>
  chat(args: OllamaChatArgs): Promise<any>
  chatCompletions(args: OllamaChatCompletionsArgs): Promise<any>
  listModels(args?: { host?: string }): Promise<any>
  showModel(args: { model: string; host?: string }): Promise<any>
  pullModel(args: {
    model: string
    insecure?: boolean
    stream?: boolean
    host?: string
  }): Promise<any>
  embeddings(args: OllamaEmbeddingsArgs): Promise<any>
  checkAvailability(host?: string): Promise<boolean>
}

export function createOllamaClient(httpRequest: HttpRequest): OllamaClient {
  const { requestAPI, checkAvailability } = createOllamaUtils(httpRequest)

  const generate = async ({
    model,
    prompt,
    options = {},
    stream = false,
    host,
  }: OllamaGenerateArgs) => {
    if (!model) throw new Error('model is required')
    if (!prompt) throw new Error('prompt is required')

    return await requestAPI({
      host,
      path: '/api/generate',
      method: 'POST',
      body: { model, prompt, options, stream },
    })
  }

  const chat = async ({
    model,
    messages,
    tools,
    think,
    options = {},
    stream = false,
    host,
  }: OllamaChatArgs) => {
    if (!model) throw new Error('model is required')
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    const body: Record<string, unknown> = { model, messages, options, stream }
    if (tools) body.tools = tools
    if (think !== undefined) body.think = think

    return await requestAPI({ host, path: '/api/chat', method: 'POST', body })
  }

  const chatCompletions = async ({
    model,
    messages,
    temperature,
    max_tokens,
    tools,
    tool_choice,
    stream = false,
    host,
  }: OllamaChatCompletionsArgs) => {
    if (!model) throw new Error('model is required')
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    const bodyParams = { model, messages, temperature, max_tokens, tools, tool_choice, stream }
    const body = Object.fromEntries(
      Object.entries(bodyParams).filter(([_, value]) => value !== undefined),
    )

    return await requestAPI({ host, path: '/v1/chat/completions', method: 'POST', body })
  }

  const listModels = async ({ host }: { host?: string } = {}) => {
    return await requestAPI({ host, path: '/api/tags', method: 'GET', body: {} })
  }

  const showModel = async ({ model, host }: { model: string; host?: string }) => {
    if (!model) throw new Error('model is required')
    return await requestAPI({
      host,
      path: '/api/show',
      method: 'POST',
      body: { name: model },
    })
  }

  const pullModel = async ({
    model,
    insecure = false,
    stream = false,
    host,
  }: {
    model: string
    insecure?: boolean
    stream?: boolean
    host?: string
  }) => {
    if (!model) throw new Error('model is required')
    return await requestAPI({
      host,
      path: '/api/pull',
      method: 'POST',
      body: { name: model, insecure, stream },
    })
  }

  const embeddings = async ({ model, prompt, options = {}, host }: OllamaEmbeddingsArgs) => {
    if (!model) throw new Error('model is required')
    if (!prompt) throw new Error('prompt is required')

    return await requestAPI({
      host,
      path: '/api/embeddings',
      method: 'POST',
      body: { model, prompt, options },
    })
  }

  return {
    generate,
    chat,
    chatCompletions,
    listModels,
    showModel,
    pullModel,
    embeddings,
    checkAvailability,
  }
}
